"use client"

import { useState, useRef, useEffect } from "react"
import styles from "./FixedFormIcon.module.css"
import formIcon from "../../assets/images/fixed-form-icon.png"
import emailjs from '@emailjs/browser'
import { countryCodes } from "../../utils/countryCodes"

/*
Sayfanın sağında sürüklenebilir bir form simgesi görüntüleyen sabit bileşen. Tıklandığında veya açıldığında, iletişim formu içeren bir modal ortaya çıkarır.
*/
export default function FixedFormIcon() {
  // Modal ve form durumu
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const formRef = useRef(null)
  
  // Ülke kodu ve telefon durumu
  const [selectedCountry, setSelectedCountry] = useState(() => (countryCodes.find(c => c.name === 'United States') || countryCodes[0]))
  const [phone, setPhone] = useState("")
  const [isCodeOpen, setIsCodeOpen] = useState(false)
  const codeWrapRef = useRef(null)
  
  // Sürüklenebilir dikey sekme durumu
  const [tabY, setTabY] = useState(50) // Üstten yüzde olarak
  const [isDragging, setIsDragging] = useState(false)
  const [hasMoved, setHasMoved] = useState(false)
  const tabRef = useRef(null)

  // Modalı aç ve sayfa kaydırmayı engelle
  const openModal = () => {
    setIsModalOpen(true)
    setIsSubmitted(false)
    document.body.style.overflow = 'hidden'
  }

  // Modalı kapat ve sayfa kaydırmayı geri aç
  const closeModal = () => {
    setIsModalOpen(false)
    document.body.style.overflow = 'unset'
  }

  // EmailJS kullanarak form verilerini gönder
  const sendEmail = (e) => {
    e.preventDefault()
    setIsSending(true)
    const fd = new FormData(formRef.current)
    const params = {
      user_name: fd.get('user_name') || '',
      user_email: fd.get('user_email') || '',
      message: fd.get('message') || '',
      user_phone: fd.get('user_phone') || '',
      country_code: fd.get('country_code') || (selectedCountry?.dial_code || ''),
      user_phone_full: `${fd.get('country_code') || (selectedCountry?.dial_code || '')} ${fd.get('user_phone') || ''}`.trim(),
    }

    emailjs
      .send(
        'service_57xto7k',
        'template_ivxg7n9',
        params,
        { publicKey: 'OPDDEgsIGDGJBTjaz' }
      )
      .then(
        () => {
          setIsSubmitted(true)
          setIsSending(false)
          formRef.current && formRef.current.reset()
          setPhone('')
        },
        (error) => {
          console.error('Email sending failed:', error)
          setIsSending(false)
          alert('There was an error sending your message. Please try again in a short while.')
        }
      )
  }

  // ISO ülke kodundan bayrak emoji oluştur
  const getFlagEmoji = (isoCode) => {
    try {
      if (!isoCode || isoCode.length !== 2) return "";
      const codePoints = isoCode
        .toUpperCase()
        .split("")
        .map(char => 127397 + char.charCodeAt(0))
      return String.fromCodePoint(...codePoints)
    } catch {
      return ""
    }
  }

  // Dışa tıklandığında ülke kodu açılır menüsünü kapat
  useEffect(() => {
    if (!isCodeOpen) return
    const onClickOutside = (e) => {
      const el = codeWrapRef.current
      if (el && !el.contains(e.target)) {
        setIsCodeOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
    }
  }, [isCodeOpen])

  // Dikey sekmenin sürüklenmesini işle
  useEffect(() => {
    if (!isDragging) return
    
    // Sürükleme hareketi
    const onMove = (e) => {
      setHasMoved(true) // Hareketin oluştuğunu işaretle
      const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY
      const windowHeight = window.innerHeight
      const newY = (clientY / windowHeight) * 100
      // %10 ile %90 arasında sınırlayın
      setTabY(Math.max(10, Math.min(90, newY)))
    }
    
    // Sürüklemeyi sonlandır
    const onEnd = () => {
      setIsDragging(false)
      // Tıklama algılaması için kısa bir gecikme sonra hasMoved'i sıfırla
      setTimeout(() => setHasMoved(false), 100)
    }
    
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onEnd)
    document.addEventListener('touchmove', onMove)
    document.addEventListener('touchend', onEnd)
    
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onEnd)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
    }
  }, [isDragging])

  // Sekme fare tuşu basıldığında sürüklemeyi başlat
  const handleTabMouseDown = (e) => {
    e.preventDefault()
    setIsDragging(true)
    setHasMoved(false)
  }

  // Sekmeye tıklandığında modalı aç
  const handleTabClick = (e) => {
    e.preventDefault()
    // Sadece sürükleme hareketi yoksa modalı aç
    if (!hasMoved && !isDragging) {
      openModal()
    }
  }

  return (
    <>
      {/* Sabit Dikey Sekme - Modal açıkken gizle */}
      {!isModalOpen && (
        <div 
          ref={tabRef}
          className={`${styles.fixedFormIcon} ${isDragging ? styles.dragging : ''}`}
          style={{ top: `${tabY}%` }}
          onMouseDown={handleTabMouseDown}
          onTouchStart={handleTabMouseDown}
          onClick={handleTabClick}
        >
          <span className={styles.formText}>Contact Us</span>
        </div>
      )}

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeModal}>
              ×
            </button>
            <div className={styles.formCard}>
              {/* Form ya da başarı mesajını göster */}
              {!isSubmitted ? (
                <>
                  <h3 className={styles.formTitle}>
                    <span className={styles.formTitleMain}>Register Your Interest</span>
                    <br />
                    <span className={styles.formTitleSub}>Contact Us For A Free Consultation</span>
                  </h3>
                  <form ref={formRef} className={styles.formGrid} onSubmit={sendEmail}>
                    <input type="text" placeholder="Full Name" required name="user_name" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2.8fr', gap: 3 }}>
                      {/* Ülke kodu seçicisi */}
                      <div className={styles.codeSelectWrap} ref={codeWrapRef}>
                        <button
                          type="button"
                          className={styles.codeSelectTrigger}
                          onClick={() => setIsCodeOpen(o => !o)}
                          aria-haspopup="listbox"
                          aria-expanded={isCodeOpen}
                        >
                          <span className={styles.codeLabel}>{selectedCountry?.name || 'United States'}</span>
                          <span className={styles.codeValue}>{selectedCountry?.dial_code || '+1'}</span>
                        </button>
                        {isCodeOpen && (
                          <ul className={styles.codeList} role="listbox">
                            {(() => {
                              const pinnedNames = [
                                'United States',
                                'Russian Federation',
                                'United Kingdom',
                                'Turkey',
                              ]
                              const pinned = countryCodes.filter(c => pinnedNames.includes(c.name))
                              const rest = countryCodes
                                .filter(c => !pinnedNames.includes(c.name))
                                .sort((a, b) => a.name.localeCompare(b.name))
                              return (
                                <>
                                  {pinned.map((c) => (
                                    <li
                                      key={'pinned-' + c.code + c.dial_code}
                                      role="option"
                                      className={styles.codeItem}
                                      onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                                    >
                                      <span className={styles.codeLabel}>{c.name}</span>
                                      <span className={styles.codeValue}>{c.dial_code}</span>
                                    </li>
                                  ))}
                                  <li className={styles.codeDivider} aria-hidden="true" />
                                  {rest.map((c) => (
                                    <li
                                      key={c.code + c.dial_code}
                                      role="option"
                                      className={styles.codeItem}
                                      onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                                    >
                                      <span className={styles.codeLabel}>{c.name}</span>
                                      <span className={styles.codeValue}>{c.dial_code}</span>
                                    </li>
                                  ))}
                                </>
                              )
                            })()}
                          </ul>
                        )}
                        <input type="hidden" name="country_code" value={selectedCountry?.dial_code || ''} />
                      </div>
                      <input
                        type="tel"
                        placeholder="Phone"
                        required
                        name="user_phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                    <input type="hidden" name="user_phone_full" value={`${selectedCountry?.dial_code || ''} ${phone}`} />
                    <input type="email" placeholder="Email" required name="user_email" />
                    <textarea placeholder="Tell us about your goals" rows="3" name="message" />
                    <button type="submit" className="primaryBtn" disabled={isSending}>
                      {isSending ? 'Sending...' : 'Submit'}
                    </button>
                  </form>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                  {/* Başarı simgesi */}
                  <div style={{ marginBottom: 12 }}>
                    <svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="32" cy="32" r="32" fill="#13A672"/><path d="M20 32l8 8 14-14" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                  <h3 className={styles.formTitle} style={{ marginTop: 0 }}>Thank you!</h3>
                  <p style={{ margin: '6px 0 0', lineHeight: 1.5 }}>
                    Your request has been received successfully.<br />
                    Our medical consultants will contact you shortly to schedule your free consultation.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
